/**
 * `/` — the one Overview, for every vertical (api#873).
 *
 * The page is a frame around GET /dashboard/layout: the band (OverviewBand —
 * four casing stats), then each widget
 * the layout returns, in its order. A `full` widget spans the row; `half`
 * widgets sit two to a row from lg and one per row below it. Each widget owns
 * its own read and its own loading, failed and empty states, so one dead
 * endpoint blanks one panel, not the page.
 *
 * The layout is per COMPANY and set by its admin in Settings > Organization >
 * Dashboard. Action Queue is locked: always on, always first.
 *
 * Two half widgets in one row are drawn at the row's height: the slot is a
 * column and the panel grows to fill it, so neither panel stops short of its
 * neighbour. The panel grows, NOT its body. fe#877 let Revenue Pulse's chart
 * take the room and reverted it: the bars scale to the period's largest
 * value, so the extra height carried no information and Smith's one Saturday
 * bar became a 217px pillar. The chart keeps its own height and the room sits
 * under it.
 *
 * Until this page a trucking tenant landed on /trucking and a rentals-only one
 * on /rentals. Both pages are still routed and still in the sidebar; `/` no
 * longer sends anyone to them.
 */
import { useAuth } from '../contexts/AuthContext';
import { OverviewBand } from '../components/overview/OverviewBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { WIDGET_REGISTRY, isRegisteredWidget } from '../components/overview/registry';
import { WidgetErrorBoundary } from '../components/overview/WidgetErrorBoundary';
import { useDashboardLayout } from '../hooks/useDashboardLayout';
import { queryFailureError, queryLoadFailure } from '../lib/load-failure';
import { resolveOverviewWidgets } from '../lib/overview-layout';

export default function OverviewScreen() {
  const { hasVertical } = useAuth();
  const layoutQ = useDashboardLayout();
  const failure = layoutQ.data === undefined ? queryLoadFailure(layoutQ) : null;
  const layout = layoutQ.data;
  const widgets = layout
    ? resolveOverviewWidgets(layout.widgets ?? [], layout.catalogue ?? [], hasVertical, isRegisteredWidget)
    : [];

  return (
    <div data-testid="overview-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global header. A dashboard: no back arrow. */}
      <OverviewBand />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px]">
          {failure ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(layoutQ)} kind={failure} subject="the Overview layout" />
            </div>
          ) : layoutQ.isLoading || layout === undefined ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className={`h-40 animate-pulse rounded-fi-panel bg-fieldiq-card ${i === 0 ? 'lg:col-span-2' : ''}`} />
              ))}
            </div>
          ) : widgets.length === 0 ? (
            <p data-testid="overview-empty" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-12 text-center text-sm text-fieldiq-muted">
              No widgets are switched on for your Overview. An admin can choose them in Settings › Organization › Dashboard.
            </p>
          ) : (
            <div data-testid="overview-grid" className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {widgets.map((w) => {
                const Widget = WIDGET_REGISTRY[w.key];
                return (
                  <div
                    key={w.key}
                    data-testid="overview-slot"
                    data-widget={w.key}
                    data-size={w.size}
                    className={`flex min-w-0 flex-col ${w.size === 'full' ? 'lg:col-span-2' : ''}`}
                  >
                    <WidgetErrorBoundary widgetKey={w.key} label={w.label}>
                      <Widget />
                    </WidgetErrorBoundary>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
